import { getClientLogContext } from '@spa/lib/logging/clientLogContext';
import readNumberEnv from '@spa/lib/configs/services/readNumberEnv';

const pinoLogLevel = process.env.NEXT_PUBLIC_PINO_LOG_LEVEL?.toLowerCase() ?? 'info';
if (
  pinoLogLevel !== 'trace' &&
  pinoLogLevel !== 'debug' &&
  pinoLogLevel !== 'info' &&
  pinoLogLevel !== 'warn' &&
  pinoLogLevel !== 'error' &&
  pinoLogLevel !== 'fatal' &&
  pinoLogLevel !== 'silent'
) {
  throw new Error('Invalid PINO_LOG_LEVEL [LogLevel: ' + pinoLogLevel + ']');
}

const isProd = process.env.NODE_ENV === 'production';

interface MinimalLogger {
  trace: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  fatal: (...args: unknown[]) => void;
}

let logger: MinimalLogger;

if (typeof window === 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-require-imports -- server-only import
  const pino: typeof import('pino') = require('pino');
  // eslint-disable-next-line @typescript-eslint/no-require-imports -- server-only import, keeps node:async_hooks out of the client/Edge bundle graph
  const requestContext: typeof import('./logging/requestContext') = require('./logging/requestContext');
  const { getRequestLogContext } = requestContext;
  type DestinationStream = import('pino').DestinationStream;
  let destination: DestinationStream | undefined;

  if (isProd) {
    const newDestination = pino.destination({ minLength: 4096, sync: false });

    const pinoFlushTimeSecs = readNumberEnv(process.env.NEXT_PUBLIC_PINO_FLUSH_SECS) ?? 1;
    const flushTimeMs = pinoFlushTimeSecs * 1000;

    process?.on('exit', () => {
      newDestination.flushSync();
    });
    setInterval(() => {
      newDestination.flush();
    }, flushTimeMs);

    destination = newDestination;
  }

  const mixin = (): Record<string, unknown> => {
    let mixinFields: Record<string, unknown> = {};
    try {
      const requestLogContext = getRequestLogContext();
      if (requestLogContext !== undefined) {
        mixinFields = { req: requestLogContext };
      }
    } catch {
      mixinFields = {};
    }
    return mixinFields;
  };

  logger = pino(
    { level: pinoLogLevel, serializers: { error: pino.stdSerializers.err }, mixin },
    destination
  );
} else {
  const noop = (): void => {};
  const levels: ReadonlyArray<keyof MinimalLogger> = [
    'trace',
    'debug',
    'info',
    'warn',
    'error',
    'fatal'
  ];
  const activeLevelIdx = pinoLogLevel === 'silent' ? levels.length : levels.indexOf(pinoLogLevel);
  const pick = (
    lvl: keyof MinimalLogger,
    sink: (...args: unknown[]) => void
  ): ((...args: unknown[]) => void) => (levels.indexOf(lvl) >= activeLevelIdx ? sink : noop);

  const hasStdio =
    typeof process !== 'undefined' &&
    typeof process.stdout?.write === 'function' &&
    typeof process.stderr?.write === 'function';

  // Only plain objects are safe structured payloads; spreading other instances may lose data.
  const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    let plain = false;

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const prototype = Object.getPrototypeOf(value);
      plain = prototype === Object.prototype || prototype === null;
    }

    return plain;
  };

  const hasToJSON = (value: unknown): value is { toJSON: () => unknown } =>
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { toJSON?: unknown }).toJSON === 'function';

  // Normalize nested special values and hostile objects without allowing logging to throw.
  const serializeForBrowserLog = (
    value: unknown,
    ancestors: ReadonlySet<unknown> = new Set()
  ): unknown => {
    let serialized: unknown;

    try {
      if (value instanceof Error) {
        serialized = { name: value.name, message: value.message, stack: value.stack };
      } else if (typeof value === 'bigint') {
        serialized = `${value.toString()}n`;
      } else if (typeof value !== 'object' || value === null) {
        serialized = value;
      } else if (ancestors.has(value)) {
        serialized = '[Circular]';
      } else {
        const nextAncestors = new Set(ancestors).add(value);
        if (Array.isArray(value)) {
          serialized = value.map((item) => serializeForBrowserLog(item, nextAncestors));
        } else if (hasToJSON(value)) {
          serialized = serializeForBrowserLog(value.toJSON(), nextAncestors);
          // Preserve collection contents that have no enumerable own keys.
        } else if (value instanceof Set) {
          serialized = Array.from(value, (item) => serializeForBrowserLog(item, nextAncestors));
        } else if (value instanceof Map) {
          serialized = Array.from(value, ([key, item]) => [
            serializeForBrowserLog(key, nextAncestors),
            serializeForBrowserLog(item, nextAncestors)
          ]);
        } else if (value instanceof URLSearchParams) {
          serialized = Object.fromEntries(value);
        } else {
          const entries: Record<string, unknown> = {};
          for (const key of Object.keys(value)) {
            try {
              entries[key] = serializeForBrowserLog(
                (value as Record<string, unknown>)[key],
                nextAncestors
              );
            } catch {
              entries[key] = '[Unserializable]';
            }
          }
          serialized = entries;
        }
      }
    } catch {
      serialized = '[Unserializable]';
    }

    return serialized;
  };

  const safeStringify = (value: unknown): string => {
    let stringified: string;

    try {
      stringified = JSON.stringify(value);
    } catch {
      stringified = '"[Unserializable]"';
    }

    return stringified;
  };

  // Stdio requires one string; browser consoles retain expandable values through the sink below.
  const format = (args: ReadonlyArray<unknown>): string => {
    let formatted: string;

    try {
      formatted =
        args
          .map((a) =>
            a instanceof Error
              ? (a.stack ?? a.message)
              : typeof a === 'bigint'
                ? `${a.toString()}n`
                : typeof a === 'object' && a !== null
                  ? safeStringify(serializeForBrowserLog(a))
                  : String(a)
          )
          .join(' ') + '\n';
    } catch {
      formatted = '[logger] failed to format log message\n';
    }

    return formatted;
  };

  const getClient = (): unknown => {
    let client: unknown = null;
    try {
      client = getClientLogContext();
    } catch {
      client = null;
    }
    return client;
  };

  const CLIENT_CONTEXT_KEY = 'client';
  const FALLBACK_CLIENT_CONTEXT_KEY = 'loggerClientContext';

  // Preserve caller-owned `client` payloads and tolerate throwing getters during the merge.
  const mergeClientContext = (
    payload: Record<string, unknown>,
    client: unknown
  ): Record<string, unknown> => {
    const contextKey = Object.prototype.hasOwnProperty.call(payload, CLIENT_CONTEXT_KEY)
      ? FALLBACK_CLIENT_CONTEXT_KEY
      : CLIENT_CONTEXT_KEY;
    let merged: Record<string, unknown>;

    try {
      merged = { ...payload, [contextKey]: client };
    } catch {
      // Recover readable fields when an eager spread triggers a throwing getter.
      const salvaged: Record<string, unknown> = {};
      for (const key of Object.keys(payload)) {
        try {
          salvaged[key] = payload[key];
        } catch {
          salvaged[key] = '[Unserializable]';
        }
      }
      salvaged[contextKey] = client;
      merged = salvaged;
    }

    return merged;
  };

  // Merge object-first calls as fields while preserving other argument shapes and client context.
  const buildLogArgs = (args: ReadonlyArray<unknown>): ReadonlyArray<unknown> => {
    let logArgs: ReadonlyArray<unknown>;

    try {
      const [first, second, ...rest] = args;
      const client = getClient();

      if (isPlainObject(first)) {
        const merged = mergeClientContext(first, client);
        logArgs =
          typeof second === 'string' ? [second, merged, ...rest] : [merged, ...args.slice(1)];
      } else {
        logArgs = [...args, { client }];
      }
    } catch {
      logArgs = [...args, { client: getClient() }];
    }

    return logArgs;
  };

  const out = hasStdio
    ? (...args: unknown[]): void => {
        process.stdout.write(format(buildLogArgs(args)));
      }
    : undefined;
  const err = hasStdio
    ? (...args: unknown[]): void => {
        process.stderr.write(format(buildLogArgs(args)));
      }
    : undefined;

  // Keep discrete browser-console arguments so objects and Error stacks remain inspectable.
  const toConsoleSink =
    (sink: (...sinkArgs: unknown[]) => void) =>
    (...args: unknown[]): void => {
      try {
        sink(...buildLogArgs(args));
      } catch {
        sink('[logger] failed to log message');
      }
    };

  logger = {
    trace: pick('trace', out ?? toConsoleSink((...a) => console.debug(...a))),
    debug: pick('debug', out ?? toConsoleSink((...a) => console.debug(...a))),
    info: pick('info', out ?? toConsoleSink((...a) => console.info(...a))),
    warn: pick('warn', err ?? toConsoleSink((...a) => console.warn(...a))),
    error: pick('error', err ?? toConsoleSink((...a) => console.error(...a))),
    fatal: pick('fatal', err ?? toConsoleSink((...a) => console.error(...a)))
  };
}

export default logger;
